<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6">
    <!-- Branch Table -->
    <div class="w-full overflow-x-auto overflow-y-auto max-h-96">
      <table class="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
        <thead>
          <tr>
            <th>No.</th>
            <th @click="sortBy('name')" class="sort-col">
              Name <span v-if="sortKey === 'name'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('tn')" class="sort-col">
              TN <span v-if="sortKey === 'tn'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('username')" class="sort-col">
              Username <span v-if="sortKey === 'username'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('email')" class="sort-col">
              Email <span v-if="sortKey === 'email'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('phone')" class="sort-col">
              Phone <span v-if="sortKey === 'phone'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('address')" class="sort-col">
              Address <span v-if="sortKey === 'address'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <th @click="sortBy('is_active')" class="sort-col">
              Status <span v-if="sortKey === 'is_active'">{{ sortOrder === 'asc' ? '↑' : '↓' }}</span>
            </th>
            <!-- <th>Due Date</th> -->
          </tr>
        </thead>
        <tbody>
          <tr v-for="(branch, index) in sortedBranches" :key="branch.id" class="hover:bg-slate-100/50 transition-colors border-b">
            <td>{{ index + 1 }}</td>
            <td>{{ branch.name }}</td>
            <td>{{ branch.tn }}</td>
            <td>{{ branch.username }}</td>
            <td>{{ branch.email || '-' }}</td>
            <td>{{ branch.phone || '-' }}</td>
            <td>{{ branch.address || '-' }}</td>
            <td>
              <span
                class="px-2 py-0.5 rounded-full text-[11px] font-semibold"
                :class="branch.is_active === 1 ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'"
              >
                {{ branch.is_active === 1 ? 'Active' : 'Blocked' }}
              </span>
            </td>
            <!-- <td>{{ formatDate(branch.due_date) }}</td> -->
          </tr>
          <tr v-if="loading">
  <td colspan="9" class="text-center py-10">
    <div class="flex items-center justify-center">
      <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
    </div>
  </td>
</tr>
          <tr v-else-if="error">
            <td colspan="9" class="text-center py-10 text-red-500">{{ error }}</td>
          </tr>
          <tr v-else-if="branches.length === 0">
            <td colspan="9" style="text-align:center; color:#94a3b8; padding: 48px 15px; font-style: italic;">
              No branches found for this company.
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const branches = ref([])
const loading = ref(false)
const error = ref('')
const sortKey = ref('name')
const sortOrder = ref('asc')

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)

const sortedBranches = computed(() => {
  const result = [...branches.value]
  result.sort((a, b) => {
    const modifier = sortOrder.value === 'asc' ? 1 : -1
    const valA = (a[sortKey.value] ?? '').toString().toLowerCase()
    const valB = (b[sortKey.value] ?? '').toString().toLowerCase()
    if (valA < valB) return -1 * modifier
    if (valA > valB) return 1 * modifier
    return 0
  })
  return result
})

function sortBy(key) {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
}

//function formatDate(value) {
//  if (!value) return '-'
//  const d = new Date(value)
//  if (Number.isNaN(d.getTime())) return '-'
//  return d.toISOString().slice(0, 10)
//}

async function loadBranches() {
  error.value = ''
  if (!companyId.value) {
    error.value = 'Company ID not found in user profile.'
    return
  }

  loading.value = true
  try {
    const response = await apiFetch(`/branches/company/${companyId.value}`, {
      method: 'GET',
    })

    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load branches')
    }

    const data = await response.json()
    branches.value = Array.isArray(data) ? data : []
  } catch (err) {
    error.value = err.message || 'Failed to load branches'
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  loadBranches()
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.9rem;
  color: #1e293b;
}

th,
td {
  padding: 5px 6px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  font-size: 0.8rem;
  white-space: nowrap;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
}

th.sort-col {
  cursor: pointer;
}
th.sort-col:hover {
  background: #e2e8f0;
}

tbody tr:hover {
  background: #f1f5f9;
}
</style>

